// ============================================================
// Bloque 46 — Canal RFW Propio y Contenido Oficial
// 5 canales oficiales para que la plataforma nunca se sienta vacía
// ============================================================

// --- Channel Type Configs ---

export interface ChannelTypeConfig {
  id: string;
  label: string;
  shortLabel: string;
  icon: string;
  color: string;
  gradient: string;
  bgLight: string;
  textColor: string;
  description: string;
  triggerRule: string;
  contentTypes: string[];
  totalContent: number;
}

export const channelTypeConfigs: ChannelTypeConfig[] = [
  {
    id: 'rfw-radio',
    label: 'RFW Radio',
    shortLabel: 'Radio',
    icon: 'ri-radio-line',
    color: 'rose',
    gradient: 'from-rose-500 via-pink-500 to-orange-400',
    bgLight: 'bg-rose-50',
    textColor: 'text-rose-600',
    description: 'Música continua, programas especiales y lo mejor de las radios de RFW cuando ninguna frecuencia está activa.',
    triggerRule: 'Se activa cuando una frecuencia está inactiva o fuera de línea',
    contentTypes: ['Música', 'Programas Especiales', 'Promociones', 'Novedades RFW', 'Ranking Semanal', 'Top Radios', 'Top Canciones'],
    totalContent: 28,
  },
  {
    id: 'rfw-tv',
    label: 'RFW TV',
    shortLabel: 'TV',
    icon: 'ri-tv-line',
    color: 'violet',
    gradient: 'from-violet-500 via-purple-500 to-fuchsia-400',
    bgLight: 'bg-violet-50',
    textColor: 'text-violet-600',
    description: 'Entrevistas exclusivas, programas grabados, eventos, tutoriales, ganadores y los clips más virales de la comunidad.',
    triggerRule: 'Se activa cuando no hay video en vivo disponible',
    contentTypes: ['Entrevistas', 'Programas Grabados', 'Eventos', 'Tutoriales', 'Ganadores', 'Top Radios', 'Top Locutores', 'Top Programas', 'Clips Virales', 'Contenido Patrocinado'],
    totalContent: 42,
  },
  {
    id: 'rfw-podcast',
    label: 'RFW Podcast',
    shortLabel: 'Podcast',
    icon: 'ri-headphone-line',
    color: 'amber',
    gradient: 'from-amber-500 via-yellow-500 to-orange-400',
    bgLight: 'bg-amber-50',
    textColor: 'text-amber-600',
    description: 'Podcasts originales producidos por RFW. Historias de radio, entrevistas profundas y análisis del mundo radial.',
    triggerRule: 'Contenido disponible permanentemente bajo demanda',
    contentTypes: ['Entrevistas', 'Historias de Radio', 'Detrás del Micrófono', 'Análisis', 'Debates', 'Especiales'],
    totalContent: 18,
  },
  {
    id: 'rfw-noticias',
    label: 'RFW Noticias',
    shortLabel: 'Noticias',
    icon: 'ri-newspaper-line',
    color: 'emerald',
    gradient: 'from-emerald-500 via-teal-500 to-cyan-400',
    bgLight: 'bg-emerald-50',
    textColor: 'text-emerald-600',
    description: 'Actualidad del mundo radial. Nuevas radios, cambios de frecuencia, lanzamientos, tecnología y tendencias.',
    triggerRule: 'Actualización continua con cobertura editorial',
    contentTypes: ['Radios Nuevas', 'Lanzamientos', 'Tecnología', 'Industria', 'Cobertura Especial', 'Entrevistas'],
    totalContent: 35,
  },
  {
    id: 'rfw-tendencias',
    label: 'RFW Tendencias',
    shortLabel: 'Tendencias',
    icon: 'ri-fire-line',
    color: 'orange',
    gradient: 'from-orange-500 via-red-500 to-rose-400',
    bgLight: 'bg-orange-50',
    textColor: 'text-orange-600',
    description: 'Lo que está sonando fuerte ahora mismo. Tendencias, rankings en vivo y lo más comentado por la comunidad.',
    triggerRule: 'Se actualiza cada 15 minutos con datos en tiempo real',
    contentTypes: ['Trending Ahora', 'Rankings en Vivo', 'Más Comentado', 'Más Compartido', 'Radios en Ascenso', 'Canciones del Momento'],
    totalContent: 22,
  },
];

// --- Content Items ---

export interface ChannelContent {
  id: string;
  channelId: string;
  type: string;
  title: string;
  subtitle: string;
  description: string;
  thumbnailUrl: string;
  duration: string;
  category: string;
  tags: string[];
  playsCount: number;
  likesCount: number;
  commentsCount: number;
  isLive: boolean;
  isExclusive: boolean;
  isSponsored: boolean;
  featured: boolean;
  publishDate: string;
  sourceRadio?: string;
  sourceHost?: string;
}

export const channelContent: ChannelContent[] = [
  // ===== RFW RADIO (8 items) =====
  {
    id: 'rr-001',
    channelId: 'rfw-radio',
    type: 'Música',
    title: 'RFW Mix: Lo Mejor de Hoy',
    subtitle: 'Éxitos actuales sin interrupciones',
    description: 'Una selección curada de los temas más pedidos en todas las radios de RFW durante las últimas 24 horas. 60 minutos de música continua sin pausas ni locución.',
    thumbnailUrl: 'https://readdy.ai/api/search-image?query=Modern%20minimalist%20radio%20studio%20with%20warm%20ambient%20lighting%2C%20professional%20broadcast%20microphone%20on%20wooden%20desk%2C%20sound%20waves%20visualization%20in%20rose%20and%20pink%20gradient%20tones%2C%20clean%20and%20modern%20aesthetic%2C%20editorial%20photography%20with%20soft%20shadows%2C%20high%20detail%2C%20harmonious%20composition%2C%20warm%20neutral%20background&width=800&height=450&seq=canal-rfw-radio-001&orientation=landscape',
    duration: '60:00',
    category: 'Música',
    tags: ['música', 'pop', 'latino', 'éxitos'],
    playsCount: 2847,
    likesCount: 342,
    commentsCount: 89,
    isLive: true,
    isExclusive: true,
    isSponsored: false,
    featured: true,
    publishDate: '2026-06-21T08:00:00Z',
  },
  {
    id: 'rr-002',
    channelId: 'rfw-radio',
    type: 'Programas Especiales',
    title: 'La Hora RFW: Especial Frecuencias',
    subtitle: 'Programa oficial de RadioFrecuenciaWeb',
    description: 'Programa semanal donde recorremos las frecuencias más emblemáticas de la plataforma. Historias, anécdotas y la música que las hizo grandes. Hoy: Las radios que cumplen aniversario esta semana.',
    thumbnailUrl: 'https://readdy.ai/api/search-image?query=Professional%20radio%20broadcast%20studio%20with%20multiple%20vintage%20radios%20displayed%20on%20shelves%2C%20warm%20golden%20lighting%2C%20professional%20headphones%20and%20microphone%20setup%2C%20cozy%20atmosphere%2C%20editorial%20photography%20with%20soft%20shadows%2C%20rich%20amber%20and%20rose%20tones%2C%20harmonious%20composition&width=800&height=450&seq=canal-rfw-radio-002&orientation=landscape',
    duration: '45:00',
    category: 'Programas',
    tags: ['programa', 'especial', 'frecuencias', 'aniversarios'],
    playsCount: 1892,
    likesCount: 267,
    commentsCount: 134,
    isLive: false,
    isExclusive: true,
    isSponsored: false,
    featured: true,
    publishDate: '2026-06-20T18:00:00Z',
  },
  {
    id: 'rr-003',
    channelId: 'rfw-radio',
    type: 'Promociones',
    title: 'Promo RFW: Doble de Puntos Este Finde',
    subtitle: 'Promoción oficial de la plataforma',
    description: 'Este fin de semana, todas las interacciones en RFW valen el doble de puntos. Comentá, compartí, participá en trivias y multiplicá tu score. Válido desde el viernes 18:00 hasta el domingo 23:59.',
    thumbnailUrl: 'https://readdy.ai/api/search-image?query=Exciting%20promotional%20offer%20visual%20with%20multiplying%20golden%20points%20and%20stars%20floating%20upward%2C%20warm%20gradient%20background%20in%20rose%20and%20gold%20tones%2C%20clean%20modern%20graphic%20design%20style%2C%20celebration%20mood%2C%20high%20detail%2C%20professional%20marketing%20visual&width=800&height=450&seq=canal-rfw-radio-003&orientation=landscape',
    duration: '2:30',
    category: 'Promociones',
    tags: ['promoción', 'puntos', 'fin de semana', 'doble'],
    playsCount: 4521,
    likesCount: 567,
    commentsCount: 203,
    isLive: false,
    isExclusive: true,
    isSponsored: false,
    featured: false,
    publishDate: '2026-06-19T12:00:00Z',
  },
  {
    id: 'rr-004',
    channelId: 'rfw-radio',
    type: 'Novedades RFW',
    title: 'Novedades RFW: Nuevas Funciones Junio 2026',
    subtitle: 'Actualización oficial de la plataforma',
    description: 'Te contamos todas las novedades que llegaron este mes: Sistema de Aniversarios, Rachas mejoradas, Bienvenida para nuevos oyentes y el nuevo Feed de Actividad Global. Escuchá los detalles en 5 minutos.',
    thumbnailUrl: 'https://readdy.ai/api/search-image?query=Clean%20modern%20tech%20update%20announcement%20visual%20with%20floating%20app%20feature%20icons%2C%20soft%20rose%20and%20coral%20gradient%20background%2C%20minimalist%20design%2C%20professional%20corporate%20communication%20style%2C%20high%20detail%2C%20harmonious%20layout&width=800&height=450&seq=canal-rfw-radio-004&orientation=landscape',
    duration: '5:15',
    category: 'Novedades',
    tags: ['novedades', 'actualización', 'junio', 'nuevas funciones'],
    playsCount: 3892,
    likesCount: 445,
    commentsCount: 178,
    isLive: false,
    isExclusive: true,
    isSponsored: false,
    featured: false,
    publishDate: '2026-06-18T10:00:00Z',
  },
  {
    id: 'rr-005',
    channelId: 'rfw-radio',
    type: 'Ranking Semanal',
    title: 'Ranking Semanal RFW: Top 20 Canciones',
    subtitle: 'Las más pedidas en todas las radios',
    description: 'El ranking oficial de RFW con las 20 canciones más solicitadas esta semana. Curado a partir de datos reales de pedidos en cabinas, encuestas y trivias. Incluye subidas, bajadas y nuevos ingresos.',
    thumbnailUrl: 'https://readdy.ai/api/search-image?query=Professional%20music%20chart%20visualization%20with%20top%2020%20list%20design%2C%20warm%20gradient%20background%20in%20rose%20and%20gold%20tones%2C%20modern%20graphic%20design%20aesthetic%2C%20clean%20typography%2C%20editorial%20style%2C%20high%20detail&width=800&height=450&seq=canal-rfw-radio-005&orientation=landscape',
    duration: '20:00',
    category: 'Rankings',
    tags: ['ranking', 'semanal', 'canciones', 'top 20'],
    playsCount: 6734,
    likesCount: 892,
    commentsCount: 345,
    isLive: false,
    isExclusive: true,
    isSponsored: false,
    featured: true,
    publishDate: '2026-06-21T06:00:00Z',
  },
  {
    id: 'rr-006',
    channelId: 'rfw-radio',
    type: 'Top Radios',
    title: 'Top 5 Radios de la Semana en RFW',
    subtitle: 'Las emisoras con más actividad',
    description: 'Conocé las 5 radios que lideraron la actividad esta semana: puntuación RFW, oyentes activos, interacciones en cabina y crecimiento de seguidores. Datos actualizados cada hora.',
    thumbnailUrl: 'https://readdy.ai/api/search-image?query=Top%20radio%20stations%20ranking%20visualization%20with%20golden%20trophy%20and%20radio%20tower%20icons%2C%20warm%20gradient%20background%2C%20professional%20broadcast%20aesthetic%2C%20clean%20modern%20design%2C%20editorial%20photography%20style%2C%20high%20detail%2C%20harmonious%20composition&width=800&height=450&seq=canal-rfw-radio-006&orientation=landscape',
    duration: '12:30',
    category: 'Rankings',
    tags: ['top radios', 'ranking', 'semanal', 'actividad'],
    playsCount: 4521,
    likesCount: 534,
    commentsCount: 212,
    isLive: false,
    isExclusive: true,
    isSponsored: false,
    featured: false,
    publishDate: '2026-06-20T14:00:00Z',
  },
  {
    id: 'rr-007',
    channelId: 'rfw-radio',
    type: 'Música',
    title: 'Sesión Acústica RFW: Andrea Valencia',
    subtitle: 'Exclusiva en vivo desde el estudio RFW',
    description: 'Andrea Valencia, locutora de Radio Nacional, nos regala una sesión acústica íntima con tres canciones originales. Grabado en vivo en el estudio central de RFW. Una producción original de Canal RFW.',
    thumbnailUrl: 'https://readdy.ai/api/search-image?query=Intimate%20acoustic%20music%20session%20in%20a%20warmly%20lit%20studio%2C%20female%20singer%20with%20guitar%2C%20soft%20golden%20spotlight%2C%20professional%20recording%20equipment%20in%20background%2C%20cozy%20atmosphere%2C%20editorial%20photography%2C%20high%20detail%2C%20harmonious%20composition&width=800&height=450&seq=canal-rfw-radio-007&orientation=landscape',
    duration: '18:45',
    category: 'Música',
    tags: ['sesión', 'acústica', 'exclusiva', 'Andrea Valencia'],
    playsCount: 3245,
    likesCount: 678,
    commentsCount: 256,
    isLive: false,
    isExclusive: true,
    isSponsored: false,
    featured: false,
    publishDate: '2026-06-19T20:00:00Z',
  },
  {
    id: 'rr-008',
    channelId: 'rfw-radio',
    type: 'Top Canciones',
    title: '#1 Esta Semana: El tema que rompió todo',
    subtitle: 'La canción más pedida en 1,438 radios',
    description: 'Análisis de la canción número 1 de la semana: por qué explotó, en qué radios se pidió más, qué clubes la adoptaron como himno y cómo se vivió en las cabinas de toda Latinoamérica.',
    thumbnailUrl: 'https://readdy.ai/api/search-image?query=Number%20one%20music%20hit%20visualization%20with%20golden%20trophy%20and%20sound%20waves%2C%20warm%20rose%20gradient%20background%2C%20modern%20graphic%20design%2C%20celebration%20mood%2C%20clean%20typography%2C%20professional%20chart%20aesthetic&width=800&height=450&seq=canal-rfw-radio-008&orientation=landscape',
    duration: '8:00',
    category: 'Rankings',
    tags: ['número 1', 'canción', 'top', 'semanal'],
    playsCount: 8934,
    likesCount: 1234,
    commentsCount: 567,
    isLive: false,
    isExclusive: true,
    isSponsored: false,
    featured: false,
    publishDate: '2026-06-21T07:00:00Z',
  },

  // ===== RFW TV (10 items) =====
  {
    id: 'rt-001',
    channelId: 'rfw-tv',
    type: 'Entrevistas',
    title: 'Cara a Cara: El Monstruo Ramírez',
    subtitle: '15 años al aire y más fuerte que nunca',
    description: 'Entrevista exclusiva con El Monstruo Ramírez, el locutor más veterano de RFW. 15 años de carrera, los secretos de su longevidad, su club de fans de 50,000 personas y su visión del futuro de la radio.',
    thumbnailUrl: 'https://readdy.ai/api/search-image?query=Professional%20studio%20interview%20setup%20with%20two%20chairs%2C%20warm%20violet%20and%20purple%20ambient%20lighting%2C%20broadcast%20cameras%20and%20professional%20equipment%2C%20modern%20TV%20studio%20aesthetic%2C%20clean%20composition%2C%20high%20detail&width=800&height=450&seq=canal-rfw-tv-001&orientation=landscape',
    duration: '35:00',
    category: 'Entrevistas',
    tags: ['entrevista', 'locutor', 'El Monstruo Ramírez', 'carrera'],
    playsCount: 12453,
    likesCount: 2345,
    commentsCount: 892,
    isLive: false,
    isExclusive: true,
    isSponsored: false,
    featured: true,
    publishDate: '2026-06-20T20:00:00Z',
  },
  {
    id: 'rt-002',
    channelId: 'rfw-tv',
    type: 'Programas Grabados',
    title: 'Carrusel Deportivo: Especial Copa América',
    subtitle: 'El mejor resumen deportivo de la radio',
    description: 'Edición especial de Carrusel Deportivo dedicada a la Copa América. Análisis, entrevistas con periodistas deportivos y las mejores jugadas comentadas por la comunidad RFW en tiempo real.',
    thumbnailUrl: 'https://readdy.ai/api/search-image?query=Dynamic%20sports%20broadcast%20studio%20with%20multiple%20screens%20showing%20soccer%20highlights%2C%20purple%20and%20violet%20ambient%20lighting%2C%20professional%20sports%20commentary%20setup%2C%20modern%20TV%20production%2C%20energetic%20atmosphere%2C%20high%20detail&width=800&height=450&seq=canal-rfw-tv-002&orientation=landscape',
    duration: '55:00',
    category: 'Deportes',
    tags: ['deportes', 'Copa América', 'Carrusel Deportivo', 'programa'],
    playsCount: 8921,
    likesCount: 1234,
    commentsCount: 567,
    isLive: false,
    isExclusive: false,
    isSponsored: false,
    featured: true,
    publishDate: '2026-06-19T21:00:00Z',
  },
  {
    id: 'rt-003',
    channelId: 'rfw-tv',
    type: 'Eventos',
    title: 'RFW Fest 2026: La Noche de las Radios',
    subtitle: 'Cobertura exclusiva del evento del año',
    description: 'Reviví la noche más importante del año radial. RFW Fest 2026 reunió a 200 emisoras, 50 locutores y más de 10,000 oyentes en una celebración épica. Entrevistas, momentos destacados y detrás de escena.',
    thumbnailUrl: 'https://readdy.ai/api/search-image?query=Large%20music%20festival%20stage%20with%20impressive%20lighting%20and%20big%20crowd%2C%20violet%20and%20purple%20stage%20lights%2C%20professional%20event%20photography%2C%20energetic%20concert%20atmosphere%2C%20wide%20angle%20shot%2C%20high%20detail&width=800&height=450&seq=canal-rfw-tv-003&orientation=landscape',
    duration: '90:00',
    category: 'Eventos',
    tags: ['RFW Fest', 'evento', 'radios', 'celebración'],
    playsCount: 23456,
    likesCount: 3456,
    commentsCount: 1234,
    isLive: false,
    isExclusive: true,
    isSponsored: true,
    featured: true,
    publishDate: '2026-06-15T22:00:00Z',
  },
  {
    id: 'rt-004',
    channelId: 'rfw-tv',
    type: 'Tutoriales',
    title: 'Tutorial RFW TV: Cómo Crear tu Radio en 10 Minutos',
    subtitle: 'Guía paso a paso para nuevos broadcasters',
    description: 'Tutorial completo para crear tu propia radio en RFW. Desde el registro hasta tu primera transmisión en vivo. Cubrimos: configuración, frecuencia, logo, programación y consejos para tus primeros oyentes.',
    thumbnailUrl: 'https://readdy.ai/api/search-image?query=Modern%20desktop%20screen%20showing%20radio%20broadcasting%20software%20interface%2C%20clean%20workspace%20with%20microphone%20and%20headphones%2C%20purple%20and%20violet%20accent%20lighting%2C%20tutorial%20style%20photography%2C%20professional%20setup%2C%20high%20detail&width=800&height=450&seq=canal-rfw-tv-004&orientation=landscape',
    duration: '10:00',
    category: 'Tutoriales',
    tags: ['tutorial', 'crear radio', 'broadcaster', 'guía'],
    playsCount: 5678,
    likesCount: 789,
    commentsCount: 234,
    isLive: false,
    isExclusive: true,
    isSponsored: false,
    featured: false,
    publishDate: '2026-06-18T16:00:00Z',
  },
  {
    id: 'rt-005',
    channelId: 'rfw-tv',
    type: 'Ganadores',
    title: 'Ganadores RFW: Historias de Éxito - Junio 2026',
    subtitle: 'Los oyentes que se llevaron los grandes premios',
    description: 'Conocé a María José, Carlos y Valentina: tres oyentes que ganaron concursos, trivias y competencias este mes. Sus historias, sus estrategias y qué significa RFW para ellos. Emoción garantizada.',
    thumbnailUrl: 'https://readdy.ai/api/search-image?query=Happy%20people%20celebrating%20winning%20awards%20in%20a%20TV%20studio%20setting%2C%20warm%20violet%20ambient%20lighting%2C%20golden%20confetti%2C%20genuine%20emotions%2C%20professional%20photography%2C%20celebration%20mood%2C%20high%20detail%2C%20harmonious%20composition&width=800&height=450&seq=canal-rfw-tv-005&orientation=landscape',
    duration: '25:00',
    category: 'Ganadores',
    tags: ['ganadores', 'historias', 'éxito', 'oyentes'],
    playsCount: 4567,
    likesCount: 678,
    commentsCount: 312,
    isLive: false,
    isExclusive: true,
    isSponsored: false,
    featured: false,
    publishDate: '2026-06-17T19:00:00Z',
  },
  {
    id: 'rt-006',
    channelId: 'rfw-tv',
    type: 'Top Radios',
    title: 'Las 10 Radios Más Influyentes de RFW',
    subtitle: 'Ranking definitivo con análisis',
    description: 'Un recorrido por las 10 emisoras que definen el pulso de RFW. Analizamos su programación, sus locutores estrella, su comunidad y los números que las ponen en la cima. Con entrevistas a sus directores.',
    thumbnailUrl: 'https://readdy.ai/api/search-image?query=Professional%20ranking%20visualization%20with%20top%2010%20radio%20station%20logos%20in%20a%20sleek%20dark%20studio%20setting%2C%20violet%20and%20gold%20accent%20lighting%2C%20modern%20broadcast%20design%2C%20professional%20TV%20graphics%2C%20high%20detail&width=800&height=450&seq=canal-rfw-tv-006&orientation=landscape',
    duration: '40:00',
    category: 'Rankings',
    tags: ['top 10', 'radios', 'influyentes', 'ranking'],
    playsCount: 9876,
    likesCount: 1234,
    commentsCount: 456,
    isLive: false,
    isExclusive: true,
    isSponsored: false,
    featured: true,
    publishDate: '2026-06-16T20:00:00Z',
  },
  {
    id: 'rt-007',
    channelId: 'rfw-tv',
    type: 'Top Locutores',
    title: 'Los 5 Locutores que Están Revolucionando la Radio',
    subtitle: 'El nuevo talento que tenés que conocer',
    description: 'Perfiles de 5 locutores emergentes que están cambiando las reglas del juego. Creatividad, conexión con la audiencia y formatos innovadores. Producido por el equipo de RFW TV.',
    thumbnailUrl: 'https://readdy.ai/api/search-image?query=Five%20diverse%20radio%20hosts%20portraits%20in%20a%20professional%20studio%20setting%2C%20warm%20violet%20and%20purple%20lighting%2C%20modern%20portrait%20photography%2C%20creative%20composition%2C%20editorial%20style%2C%20high%20detail%2C%20clean%20background&width=800&height=450&seq=canal-rfw-tv-007&orientation=landscape',
    duration: '30:00',
    category: 'Perfiles',
    tags: ['locutores', 'talento', 'emergentes', 'perfiles'],
    playsCount: 5678,
    likesCount: 890,
    commentsCount: 345,
    isLive: false,
    isExclusive: true,
    isSponsored: false,
    featured: false,
    publishDate: '2026-06-15T18:00:00Z',
  },
  {
    id: 'rt-008',
    channelId: 'rfw-tv',
    type: 'Top Programas',
    title: 'Top 3 Programas que No te Podés Perder',
    subtitle: 'La mejor programación de la semana',
    description: 'Seleccionamos los 3 programas más destacados de la semana: innovación, engagement y calidad de contenido. Con fragmentos exclusivos de cada uno y entrevistas con sus creadores.',
    thumbnailUrl: 'https://readdy.ai/api/search-image?query=Professional%20TV%20program%20showcase%20with%20three%20split%20screens%20showing%20different%20radio%20programs%2C%20violet%20gradient%20background%2C%20modern%20broadcast%20graphics%2C%20clean%20design%2C%20editorial%20style%2C%20high%20detail&width=800&height=450&seq=canal-rfw-tv-008&orientation=landscape',
    duration: '22:00',
    category: 'Programas',
    tags: ['top programas', 'semanal', 'destacados', 'programación'],
    playsCount: 6543,
    likesCount: 876,
    commentsCount: 321,
    isLive: false,
    isExclusive: true,
    isSponsored: false,
    featured: false,
    publishDate: '2026-06-14T17:00:00Z',
  },
  {
    id: 'rt-009',
    channelId: 'rfw-tv',
    type: 'Clips Virales',
    title: 'Lo Más Viral de RFW: Junio 2026',
    subtitle: 'Los momentos que explotaron este mes',
    description: 'Recopilación de los 20 clips más virales del mes: momentos épicos en cabina, reacciones en vivo, ganadores emocionados, trivias imposibles y los mejores memes de la comunidad RFW.',
    thumbnailUrl: 'https://readdy.ai/api/search-image?query=Dynamic%20viral%20video%20compilation%20visual%20with%20fire%20and%20explosion%20effects%2C%20violet%20and%20orange%20gradient%20background%2C%20modern%20social%20media%20aesthetic%2C%20energetic%20mood%2C%20professional%20editing%20style%2C%20high%20detail&width=800&height=450&seq=canal-rfw-tv-009&orientation=landscape',
    duration: '15:00',
    category: 'Viral',
    tags: ['viral', 'clips', 'momentos', 'junio'],
    playsCount: 15678,
    likesCount: 2345,
    commentsCount: 987,
    isLive: false,
    isExclusive: true,
    isSponsored: false,
    featured: false,
    publishDate: '2026-06-13T20:00:00Z',
  },
  {
    id: 'rt-010',
    channelId: 'rfw-tv',
    type: 'Contenido Patrocinado',
    title: 'Presentado por AudioTech: El Futuro del Streaming Radial',
    subtitle: 'Contenido patrocinado',
    description: 'AudioTech, líder en tecnología de streaming, nos muestra cómo está transformando la experiencia de escuchar radio online. Equipos, software y las tendencias que vienen. Producido en colaboración con RFW TV.',
    thumbnailUrl: 'https://readdy.ai/api/search-image?query=Futuristic%20audio%20streaming%20technology%20visualization%20with%20sleek%20devices%20and%20sound%20waves%2C%20violet%20and%20silver%20gradient%20background%2C%20professional%20product%20photography%2C%20modern%20tech%20aesthetic%2C%20clean%20composition%2C%20high%20detail&width=800&height=450&seq=canal-rfw-tv-010&orientation=landscape',
    duration: '12:00',
    category: 'Patrocinado',
    tags: ['patrocinado', 'AudioTech', 'streaming', 'tecnología'],
    playsCount: 3456,
    likesCount: 432,
    commentsCount: 123,
    isLive: false,
    isExclusive: false,
    isSponsored: true,
    featured: false,
    publishDate: '2026-06-12T16:00:00Z',
  },

  // ===== RFW PODCAST (6 items) =====
  {
    id: 'rp-001',
    channelId: 'rfw-podcast',
    type: 'Historias de Radio',
    title: 'Episodio 1: El Día que la Radio Cambió para Siempre',
    subtitle: 'Historia oral del nacimiento de RFW',
    description: 'El primer episodio de nuestra serie documental. Cómo nació RadioFrecuenciaWeb, contado por sus fundadores, los primeros broadcasters y los oyentes que estuvieron desde el día 1. Una historia de pasión por la radio.',
    thumbnailUrl: 'https://readdy.ai/api/search-image?query=Vintage%20radio%20equipment%20in%20a%20warm%20nostalgic%20setting%2C%20old%20microphones%20and%20mixing%20consoles%2C%20amber%20and%20golden%20lighting%2C%20documentary%20photography%20style%2C%20warm%20atmosphere%2C%20high%20detail%2C%20harmonious%20composition&width=800&height=450&seq=canal-rfw-podcast-001&orientation=landscape',
    duration: '45:00',
    category: 'Documental',
    tags: ['historia', 'RFW', 'fundación', 'documental'],
    playsCount: 3456,
    likesCount: 567,
    commentsCount: 234,
    isLive: false,
    isExclusive: true,
    isSponsored: false,
    featured: true,
    publishDate: '2026-06-20T10:00:00Z',
  },
  {
    id: 'rp-002',
    channelId: 'rfw-podcast',
    type: 'Entrevistas',
    title: 'Detrás del Micrófono: Directores de Radio',
    subtitle: 'Lo que no se ve de dirigir una emisora',
    description: 'Entrevistamos a 3 directores de las radios más grandes de RFW. Hablamos de desafíos, programación, competencia en vivo, manejo de comunidades y el futuro de la radio online. Una conversación íntima y reveladora.',
    thumbnailUrl: 'https://readdy.ai/api/search-image?query=Intimate%20podcast%20recording%20scene%20with%20professional%20microphones%20and%20cozy%20studio%20lighting%2C%20amber%20warm%20tones%2C%20conversation%20setup%20with%20chairs%2C%20professional%20audio%20equipment%2C%20editorial%20photography%2C%20high%20detail&width=800&height=450&seq=canal-rfw-podcast-002&orientation=landscape',
    duration: '55:00',
    category: 'Entrevistas',
    tags: ['entrevista', 'directores', 'radio', 'detrás de escena'],
    playsCount: 2345,
    likesCount: 345,
    commentsCount: 156,
    isLive: false,
    isExclusive: true,
    isSponsored: false,
    featured: true,
    publishDate: '2026-06-18T08:00:00Z',
  },
  {
    id: 'rp-003',
    channelId: 'rfw-podcast',
    type: 'Análisis',
    title: 'El Estado de la Radio Online en 2026',
    subtitle: 'Tendencias, datos y predicciones',
    description: 'Análisis profundo del panorama radial online: 1,438 radios en RFW, 3,210 programas activos, 4,782 locutores. Datos, tendencias de consumo, tecnología y hacia dónde va la industria en Latinoamérica.',
    thumbnailUrl: 'https://readdy.ai/api/search-image?query=Data%20visualization%20and%20analytics%20dashboard%20in%20a%20professional%20studio%20setting%2C%20amber%20and%20golden%20accent%20lighting%2C%20modern%20data%20journalism%20aesthetic%2C%20clean%20graphs%20and%20charts%2C%20professional%20photography%2C%20high%20detail&width=800&height=450&seq=canal-rfw-podcast-003&orientation=landscape',
    duration: '65:00',
    category: 'Análisis',
    tags: ['análisis', 'industria', 'radio online', 'datos'],
    playsCount: 2345,
    likesCount: 432,
    commentsCount: 187,
    isLive: false,
    isExclusive: true,
    isSponsored: false,
    featured: false,
    publishDate: '2026-06-16T12:00:00Z',
  },
  {
    id: 'rp-004',
    channelId: 'rfw-podcast',
    type: 'Debates',
    title: 'La Mesa Redonda: ¿El Futuro de la FM?',
    subtitle: 'Debate con 4 expertos de la industria',
    description: '¿Tiene futuro la FM tradicional frente al streaming? ¿Qué ventajas tiene cada formato? 4 expertos debaten sobre el presente y futuro de la radiodifusión. Moderado por el equipo de RFW Podcast.',
    thumbnailUrl: 'https://readdy.ai/api/search-image?query=Round%20table%20discussion%20with%20four%20diverse%20professionals%20in%20a%20modern%20studio%2C%20warm%20amber%20lighting%2C%20professional%20debate%20atmosphere%2C%20editorial%20photography%2C%20clean%20composition%2C%20high%20detail%2C%20harmonious%20layout&width=800&height=450&seq=canal-rfw-podcast-004&orientation=landscape',
    duration: '50:00',
    category: 'Debate',
    tags: ['debate', 'FM', 'streaming', 'futuro'],
    playsCount: 1876,
    likesCount: 298,
    commentsCount: 145,
    isLive: false,
    isExclusive: true,
    isSponsored: false,
    featured: false,
    publishDate: '2026-06-14T15:00:00Z',
  },
  {
    id: 'rp-005',
    channelId: 'rfw-podcast',
    type: 'Especiales',
    title: 'Especial: Mujeres que Transforman la Radio',
    subtitle: 'Historias de liderazgo femenino en RFW',
    description: 'Episodio especial dedicado a las mujeres que están liderando el cambio en la radio online. Locutoras, directoras, productoras y community managers comparten sus experiencias y su visión del medio.',
    thumbnailUrl: 'https://readdy.ai/api/search-image?query=Professional%20portrait%20of%20diverse%20women%20in%20broadcast%20studio%20setting%2C%20warm%20amber%20and%20golden%20lighting%2C%20empowering%20atmosphere%2C%20editorial%20photography%20style%2C%20clean%20composition%2C%20high%20detail%2C%20professional%20mood&width=800&height=450&seq=canal-rfw-podcast-005&orientation=landscape',
    duration: '40:00',
    category: 'Especial',
    tags: ['mujeres', 'liderazgo', 'radio', 'diversidad'],
    playsCount: 2345,
    likesCount: 456,
    commentsCount: 198,
    isLive: false,
    isExclusive: true,
    isSponsored: false,
    featured: false,
    publishDate: '2026-06-12T10:00:00Z',
  },
  {
    id: 'rp-006',
    channelId: 'rfw-podcast',
    type: 'Detrás del Micrófono',
    title: 'Ruta RFW: Viajando por las Radios de América',
    subtitle: 'De México a Argentina, las voces del continente',
    description: 'Un viaje sonoro por 8 países y 20 ciudades. Recorremos el continente americano a través de sus radios en RFW. Culturas, acentos, música y las historias que cada frecuencia cuenta.',
    thumbnailUrl: 'https://readdy.ai/api/search-image?query=Artistic%20map%20of%20Latin%20America%20with%20radio%20towers%20and%20sound%20waves%20connecting%20cities%2C%20amber%20and%20warm%20golden%20gradient%2C%20travel%20documentary%20aesthetic%2C%20professional%20design%2C%20high%20detail%2C%20harmonious%20composition&width=800&height=450&seq=canal-rfw-podcast-006&orientation=landscape',
    duration: '70:00',
    category: 'Documental',
    tags: ['viaje', 'américa', 'radios', 'continente'],
    playsCount: 1234,
    likesCount: 234,
    commentsCount: 98,
    isLive: false,
    isExclusive: true,
    isSponsored: false,
    featured: false,
    publishDate: '2026-06-10T09:00:00Z',
  },

  // ===== RFW NOTICIAS (8 items) =====
  {
    id: 'rn-001',
    channelId: 'rfw-noticias',
    type: 'Radios Nuevas',
    title: '47 Nuevas Radios se Unieron a RFW Esta Semana',
    subtitle: 'Récord de incorporaciones en junio',
    description: 'La plataforma alcanzó un récord de nuevas emisoras incorporadas en una semana. Análisis por país, género musical y tipo de programación. Conocé las 5 más prometedoras según nuestro equipo editorial.',
    thumbnailUrl: 'https://readdy.ai/api/search-image?query=Multiple%20radio%20station%20logos%20arranged%20in%20a%20grid%20pattern%20with%20growth%20chart%20overlay%2C%20emerald%20and%20teal%20gradient%20background%2C%20modern%20news%20graphics%20style%2C%20professional%20editorial%20design%2C%20high%20detail&width=800&height=450&seq=canal-rfw-noticias-001&orientation=landscape',
    duration: '8:00',
    category: 'Plataforma',
    tags: ['nuevas radios', 'récord', 'junio', 'crecimiento'],
    playsCount: 5678,
    likesCount: 789,
    commentsCount: 345,
    isLive: false,
    isExclusive: true,
    isSponsored: false,
    featured: true,
    publishDate: '2026-06-21T09:00:00Z',
  },
  {
    id: 'rn-002',
    channelId: 'rfw-noticias',
    type: 'Lanzamientos',
    title: 'RFW Lanza Sistema de Aniversarios',
    subtitle: 'Nueva funcionalidad para celebrar a la comunidad',
    description: 'La plataforma presenta su nuevo Sistema de Aniversarios, que detecta automáticamente fechas especiales de radios, programas, locutores, clubes y usuarios. Sin configuración, sin intervención manual.',
    thumbnailUrl: 'https://readdy.ai/api/search-image?query=Modern%20product%20launch%20announcement%20visual%20with%20celebration%20elements%2C%20emerald%20and%20green%20gradient%20background%2C%20clean%20tech%20news%20aesthetic%2C%20professional%20corporate%20design%2C%20high%20detail%2C%20harmonious%20composition&width=800&height=450&seq=canal-rfw-noticias-002&orientation=landscape',
    duration: '5:00',
    category: 'Producto',
    tags: ['lanzamiento', 'aniversarios', 'nueva función', 'RFW'],
    playsCount: 4567,
    likesCount: 678,
    commentsCount: 234,
    isLive: false,
    isExclusive: true,
    isSponsored: false,
    featured: true,
    publishDate: '2026-06-19T11:00:00Z',
  },
  {
    id: 'rn-003',
    channelId: 'rfw-noticias',
    type: 'Tecnología',
    title: 'RFW Mejora su Calidad de Streaming un 40%',
    subtitle: 'Nueva infraestructura técnica implementada',
    description: 'La plataforma completó la migración a nuevos servidores con latencia reducida y mayor estabilidad. Los oyentes notarán mejor calidad de audio, menos cortes y carga más rápida en todas las frecuencias.',
    thumbnailUrl: 'https://readdy.ai/api/search-image?query=Modern%20server%20room%20with%20blinking%20lights%20and%20network%20cables%2C%20emerald%20and%20cyan%20accent%20lighting%2C%20technology%20infrastructure%20photography%2C%20clean%20professional%20aesthetic%2C%20high%20detail%2C%20harmonious%20composition&width=800&height=450&seq=canal-rfw-noticias-003&orientation=landscape',
    duration: '4:30',
    category: 'Tecnología',
    tags: ['tecnología', 'streaming', 'infraestructura', 'mejora'],
    playsCount: 3892,
    likesCount: 534,
    commentsCount: 187,
    isLive: false,
    isExclusive: true,
    isSponsored: false,
    featured: false,
    publishDate: '2026-06-17T14:00:00Z',
  },
  {
    id: 'rn-004',
    channelId: 'rfw-noticias',
    type: 'Industria',
    title: 'Latinoamérica Lidera el Crecimiento de Radio Online',
    subtitle: 'Informe especial: la región crece 3x más rápido',
    description: 'Según datos internos de RFW, Latinoamérica está experimentando un crecimiento 3 veces superior al promedio global en consumo de radio online. México, Colombia y Argentina lideran el ranking.',
    thumbnailUrl: 'https://readdy.ai/api/search-image?query=Map%20of%20Latin%20America%20with%20growth%20arrows%20and%20data%20visualization%20overlay%2C%20emerald%20and%20teal%20gradient%20background%2C%20professional%20business%20news%20graphics%2C%20clean%20design%2C%20high%20detail%2C%20editorial%20style&width=800&height=450&seq=canal-rfw-noticias-004&orientation=landscape',
    duration: '7:00',
    category: 'Industria',
    tags: ['Latinoamérica', 'crecimiento', 'radio online', 'informe'],
    playsCount: 4567,
    likesCount: 678,
    commentsCount: 289,
    isLive: false,
    isExclusive: true,
    isSponsored: false,
    featured: false,
    publishDate: '2026-06-15T10:00:00Z',
  },
  {
    id: 'rn-005',
    channelId: 'rfw-noticias',
    type: 'Cobertura Especial',
    title: 'Cobertura Especial: RFW en la Copa América 2026',
    subtitle: 'Cómo las radios están viviendo el torneo',
    description: 'Reporte especial sobre cómo las radios deportivas de RFW están cubriendo la Copa América. Entrevistas con locutores, datos de audiencia en tiempo real y las mejores transmisiones en vivo del torneo.',
    thumbnailUrl: 'https://readdy.ai/api/search-image?query=Dynamic%20soccer%20stadium%20atmosphere%20with%20broadcast%20equipment%20in%20foreground%2C%20emerald%20and%20green%20accent%20lighting%2C%20sports%20journalism%20photography%2C%20professional%20event%20coverage%2C%20high%20detail%2C%20energetic%20mood&width=800&height=450&seq=canal-rfw-noticias-005&orientation=landscape',
    duration: '12:00',
    category: 'Deportes',
    tags: ['Copa América', 'fútbol', 'cobertura', 'radios deportivas'],
    playsCount: 7890,
    likesCount: 1234,
    commentsCount: 567,
    isLive: false,
    isExclusive: true,
    isSponsored: false,
    featured: true,
    publishDate: '2026-06-14T18:00:00Z',
  },
  {
    id: 'rn-006',
    channelId: 'rfw-noticias',
    type: 'Entrevistas',
    title: 'El Creador de RFW Habla del Futuro de la Plataforma',
    subtitle: 'Entrevista exclusiva: la visión a 5 años',
    description: 'El fundador de RadioFrecuenciaWeb comparte su visión del futuro: IA en la radio, monetización para broadcasters, expansión global y el plan para llegar a 10,000 radios activas en 2028.',
    thumbnailUrl: 'https://readdy.ai/api/search-image?query=Professional%20CEO%20portrait%20in%20a%20modern%20office%20with%20large%20windows%2C%20emerald%20and%20teal%20accent%20lighting%2C%20corporate%20interview%20setting%2C%20confident%20and%20visionary%20mood%2C%20editorial%20photography%2C%20high%20detail&width=800&height=450&seq=canal-rfw-noticias-006&orientation=landscape',
    duration: '22:00',
    category: 'Entrevistas',
    tags: ['entrevista', 'fundador', 'futuro', 'visión'],
    playsCount: 6789,
    likesCount: 987,
    commentsCount: 432,
    isLive: false,
    isExclusive: true,
    isSponsored: false,
    featured: false,
    publishDate: '2026-06-12T16:00:00Z',
  },
  {
    id: 'rn-007',
    channelId: 'rfw-noticias',
    type: 'Tecnología',
    title: 'Inteligencia Artificial en la Radio: El Futuro Ya Está Acá',
    subtitle: 'Cómo la IA está transformando las cabinas',
    description: 'Reportaje especial sobre el uso de inteligencia artificial en las radios de RFW: desde asistentes de programación hasta generación de contenido y análisis de audiencia en tiempo real.',
    thumbnailUrl: 'https://readdy.ai/api/search-image?query=Futuristic%20AI%20visualization%20in%20a%20broadcast%20studio%20context%2C%20holographic%20data%20displays%2C%20emerald%20and%20cyan%20gradient%2C%20modern%20tech%20journalism%20aesthetic%2C%20professional%20photography%2C%20high%20detail%2C%20clean%20composition&width=800&height=450&seq=canal-rfw-noticias-007&orientation=landscape',
    duration: '15:00',
    category: 'Tecnología',
    tags: ['IA', 'inteligencia artificial', 'radio', 'futuro'],
    playsCount: 4567,
    likesCount: 654,
    commentsCount: 298,
    isLive: false,
    isExclusive: true,
    isSponsored: false,
    featured: false,
    publishDate: '2026-06-10T12:00:00Z',
  },
  {
    id: 'rn-008',
    channelId: 'rfw-noticias',
    type: 'Radios Nuevas',
    title: 'De Garage a RFW: La Historia de Radio Independencia',
    subtitle: 'Cómo una radio barrial llegó a 100,000 oyentes',
    description: 'La inspiradora historia de Radio Independencia, una emisora comunitaria que empezó en un garage de Medellín y hoy es una de las 50 más escuchadas de RFW. Crecimiento orgánico, comunidad y pasión.',
    thumbnailUrl: 'https://readdy.ai/api/search-image?query=Small%20garage%20transformed%20into%20a%20radio%20studio%20with%20passionate%20broadcasters%2C%20emerald%20and%20warm%20lighting%2C%20community%20radio%20vibe%2C%20documentary%20photography%20style%2C%20authentic%20atmosphere%2C%20high%20detail%2C%20inspirational%20mood&width=800&height=450&seq=canal-rfw-noticias-008&orientation=landscape',
    duration: '18:00',
    category: 'Historias',
    tags: ['historia', 'radio comunitaria', 'Medellín', 'crecimiento'],
    playsCount: 3456,
    likesCount: 567,
    commentsCount: 234,
    isLive: false,
    isExclusive: true,
    isSponsored: false,
    featured: false,
    publishDate: '2026-06-08T10:00:00Z',
  },

  // ===== RFW TENDENCIAS (6 items) =====
  {
    id: 'rtd-001',
    channelId: 'rfw-tendencias',
    type: 'Trending Ahora',
    title: '#1 Trending: La Mega cumple 18 años',
    subtitle: 'La comunidad celebra en masa',
    description: 'La Mega FM está siendo tendencia absoluta hoy. Su aniversario número 18 generó más de 5,000 felicitaciones en 6 horas. Los clubes de fans organizaron trivias especiales y los locutores recibieron mensajes de todo el continente.',
    thumbnailUrl: 'https://readdy.ai/api/search-image?query=Celebration%20scene%20with%20birthday%20elements%20and%20radio%20theme%2C%20orange%20and%20rose%20gradient%20background%2C%20vibrant%20social%20media%20trending%20aesthetic%2C%20energetic%20mood%2C%20professional%20design%2C%20high%20detail&width=800&height=450&seq=canal-rfw-tendencias-001&orientation=landscape',
    duration: '3:00',
    category: 'Trending',
    tags: ['trending', 'La Mega', 'aniversario', '18 años'],
    playsCount: 12345,
    likesCount: 2345,
    commentsCount: 987,
    isLive: true,
    isExclusive: true,
    isSponsored: false,
    featured: true,
    publishDate: '2026-06-21T10:00:00Z',
  },
  {
    id: 'rtd-002',
    channelId: 'rfw-tendencias',
    type: 'Rankings en Vivo',
    title: 'Ranking en Vivo: Top 10 Radios Ahora',
    subtitle: 'Actualizado cada 15 minutos',
    description: 'Las 10 radios con más oyentes activos en este momento. Datos en tiempo real con tendencia (subiendo, estable, bajando). La competencia está más reñida que nunca este sábado.',
    thumbnailUrl: 'https://readdy.ai/api/search-image?query=Live%20ranking%20leaderboard%20visualization%20with%20moving%20numbers%20and%20arrows%2C%20orange%20and%20red%20gradient%20background%2C%20modern%20data%20dashboard%20aesthetic%2C%20dynamic%20and%20energetic%2C%20professional%20design%2C%20high%20detail&width=800&height=450&seq=canal-rfw-tendencias-002&orientation=landscape',
    duration: 'Live',
    category: 'Rankings',
    tags: ['ranking', 'en vivo', 'top 10', 'radios'],
    playsCount: 8765,
    likesCount: 1234,
    commentsCount: 456,
    isLive: true,
    isExclusive: true,
    isSponsored: false,
    featured: true,
    publishDate: '2026-06-21T11:00:00Z',
  },
  {
    id: 'rtd-003',
    channelId: 'rfw-tendencias',
    type: 'Más Comentado',
    title: 'El Debate del Día: ¿FM o Streaming?',
    subtitle: 'Más de 2,000 comentarios en 3 horas',
    description: 'La comunidad está encendida. Un post en el Centro Social preguntando "¿Preferís FM tradicional o streaming?" ya acumula más de 2,000 comentarios. Los argumentos más votados y las opiniones de expertos.',
    thumbnailUrl: 'https://readdy.ai/api/search-image?query=Social%20media%20debate%20visualization%20with%20comment%20threads%20and%20engagement%20metrics%2C%20orange%20and%20amber%20gradient%20background%2C%20modern%20social%20network%20aesthetic%2C%20dynamic%20and%20engaging%2C%20professional%20design%2C%20high%20detail&width=800&height=450&seq=canal-rfw-tendencias-003&orientation=landscape',
    duration: '5:00',
    category: 'Social',
    tags: ['debate', 'FM', 'streaming', 'comentarios'],
    playsCount: 6789,
    likesCount: 987,
    commentsCount: 2134,
    isLive: false,
    isExclusive: true,
    isSponsored: false,
    featured: false,
    publishDate: '2026-06-21T08:00:00Z',
  },
  {
    id: 'rtd-004',
    channelId: 'rfw-tendencias',
    type: 'Más Compartido',
    title: 'El Clip Viral de la Semana: Sorpresa en Cabina',
    subtitle: 'Compartido 3,400 veces en 48 horas',
    description: 'Una oyente le pidió matrimonio a su pareja en plena cabina de Radio Amor. El momento fue capturado en video y ya es lo más compartido de la semana. La historia completa, con entrevista a los protagonistas.',
    thumbnailUrl: 'https://readdy.ai/api/search-image?query=Romantic%20surprise%20proposal%20moment%20in%20a%20radio%20studio%2C%20genuine%20emotion%2C%20orange%20and%20warm%20golden%20lighting%2C%20heartwarming%20photography%2C%20viral%20moment%20aesthetic%2C%20high%20detail%2C%20emotional%20mood&width=800&height=450&seq=canal-rfw-tendencias-004&orientation=landscape',
    duration: '4:30',
    category: 'Viral',
    tags: ['viral', 'propuesta', 'cabina', 'compartido'],
    playsCount: 15678,
    likesCount: 3456,
    commentsCount: 1234,
    isLive: false,
    isExclusive: true,
    isSponsored: false,
    featured: false,
    publishDate: '2026-06-20T15:00:00Z',
  },
  {
    id: 'rtd-005',
    channelId: 'rfw-tendencias',
    type: 'Radios en Ascenso',
    title: 'Radios en Ascenso: 5 Emisoras que Crecen Rápido',
    subtitle: 'Las revelaciones de junio 2026',
    description: 'Identificamos 5 radios que están experimentando un crecimiento explosivo este mes. Qué están haciendo bien, cómo construyen su comunidad y por qué deberías sintonizarlas ahora mismo.',
    thumbnailUrl: 'https://readdy.ai/api/search-image?query=Growth%20chart%20with%20five%20rising%20lines%20and%20radio%20icons%2C%20orange%20and%20red%20gradient%20background%2C%20modern%20business%20analytics%20aesthetic%2C%20professional%20design%2C%20high%20detail%2C%20upward%20momentum%20mood&width=800&height=450&seq=canal-rfw-tendencias-005&orientation=landscape',
    duration: '8:00',
    category: 'Descubrimiento',
    tags: ['ascenso', 'radios', 'crecimiento', 'junio'],
    playsCount: 4567,
    likesCount: 678,
    commentsCount: 234,
    isLive: false,
    isExclusive: true,
    isSponsored: false,
    featured: false,
    publishDate: '2026-06-19T14:00:00Z',
  },
  {
    id: 'rtd-006',
    channelId: 'rfw-tendencias',
    type: 'Canciones del Momento',
    title: 'Top 5 Canciones que Estallaron Esta Semana',
    subtitle: 'Lo que más se pidió en cabinas',
    description: 'Las 5 canciones que más crecieron en pedidos esta semana. De reggaetón a rock, de cumbia a electrónica. Con fragmentos de audio y los datos de en qué radios y países se pidieron más.',
    thumbnailUrl: 'https://readdy.ai/api/search-image?query=Music%20chart%20top%205%20visualization%20with%20album%20covers%20and%20sound%20waves%2C%20orange%20and%20rose%20gradient%20background%2C%20modern%20music%20industry%20aesthetic%2C%20dynamic%20design%2C%20professional%2C%20high%20detail%2C%20energetic%20mood&width=800&height=450&seq=canal-rfw-tendencias-006&orientation=landscape',
    duration: '6:00',
    category: 'Música',
    tags: ['canciones', 'top 5', 'cabinas', 'semanal'],
    playsCount: 5678,
    likesCount: 789,
    commentsCount: 345,
    isLive: false,
    isExclusive: true,
    isSponsored: false,
    featured: false,
    publishDate: '2026-06-18T11:00:00Z',
  },
];

// --- Stats ---

export interface CanalRFWStats {
  totalContent: number;
  activeNow: number;
  liveNow: number;
  exclusiveContent: number;
  sponsoredContent: number;
  totalPlays: number;
  totalLikes: number;
  totalComments: number;
  channelsActive: number;
  topChannel: string;
  topChannelPlays: number;
  avgDuration: string;
  updatedEvery: string;
}

export const canalRFWStats: CanalRFWStats = {
  totalContent: 38,
  activeNow: 35,
  liveNow: 3,
  exclusiveContent: 35,
  sponsoredContent: 2,
  totalPlays: 234567,
  totalLikes: 34567,
  totalComments: 13456,
  channelsActive: 5,
  topChannel: 'RFW Radio',
  topChannelPlays: 45678,
  avgDuration: '18:30',
  updatedEvery: '15 minutos',
};

// --- Rules ---

export interface CanalRFWRule {
  id: string;
  icon: string;
  color: string;
  title: string;
  description: string;
  trigger: string;
}

export const canalRFWRules: CanalRFWRule[] = [
  {
    id: 'rule-1',
    icon: 'ri-signal-wifi-off-line',
    color: 'rose',
    title: 'Si una radio no tiene frecuencia activa',
    description: 'El sistema detecta automáticamente la inactividad y, en lugar de mostrar una pantalla vacía, reproduce contenido de RFW Radio: música, rankings, promociones y programas especiales. El oyente nunca se va con las manos vacías.',
    trigger: 'Trigger: Frecuencia inactiva > 30 segundos',
  },
  {
    id: 'rule-2',
    icon: 'ri-stop-circle-line',
    color: 'violet',
    title: 'Si no existe video en vivo',
    description: 'Cuando ninguna transmisión de video está activa, RFW TV toma el control automáticamente. Entrevistas grabadas, eventos pasados, tutoriales, clips virales y contenido exclusivo llenan la pantalla. Siempre hay algo para ver.',
    trigger: 'Trigger: Sin video en vivo > 15 segundos',
  },
  {
    id: 'rule-3',
    icon: 'ri-heart-pulse-line',
    color: 'emerald',
    title: 'La plataforma nunca debe sentirse vacía',
    description: 'RFW Podcast, RFW Noticias y RFW Tendencias completan el ecosistema de contenido propio. Cinco canales oficiales que garantizan que cada visita tenga algo valioso: escuchar, ver, leer o descubrir. RFW no solo conecta radios — también crea contenido.',
    trigger: 'Principio: Disponibilidad 24/7 en toda la plataforma',
  },
];

// --- Location configs where Canal RFW appears ---

export interface CanalLocation {
  id: string;
  label: string;
  icon: string;
  description: string;
  path: string;
  primaryChannel: string;
}

export const canalLocations: CanalLocation[] = [
  {
    id: 'landing',
    label: 'Landing',
    icon: 'ri-home-4-line',
    description: 'Sección destacada en la landing con los 5 canales y contenido destacado del momento.',
    path: '/',
    primaryChannel: 'rfw-tendencias',
  },
  {
    id: 'app-oyente',
    label: 'App Oyente',
    icon: 'ri-smartphone-line',
    description: 'Cuando no hay radio sintonizada, RFW Radio se activa automáticamente con música y rankings.',
    path: '/app',
    primaryChannel: 'rfw-radio',
  },
  {
    id: 'app-tv',
    label: 'App TV',
    icon: 'ri-tv-2-line',
    description: 'Cuando no hay transmisión en vivo, RFW TV muestra contenido grabado y entrevistas exclusivas.',
    path: '/tv-app',
    primaryChannel: 'rfw-tv',
  },
  {
    id: 'explorar',
    label: 'Explorar',
    icon: 'ri-compass-3-line',
    description: 'Canal RFW aparece como opción de descubrimiento junto a las radios de la comunidad.',
    path: '/explorar',
    primaryChannel: 'rfw-radio',
  },
  {
    id: 'tendencias',
    label: 'Tendencias',
    icon: 'ri-fire-line',
    description: 'RFW Tendencias es la fuente principal de datos en tiempo real sobre lo que está pasando.',
    path: '/actividad-global',
    primaryChannel: 'rfw-tendencias',
  },
  {
    id: 'inicio',
    label: 'Inicio',
    icon: 'ri-play-circle-line',
    description: 'RFW Noticias aparece en la sección de inicio con las últimas novedades editoriales.',
    path: '/',
    primaryChannel: 'rfw-noticias',
  },
];

// --- Helpers ---

export function getContentByChannel(channelId: string): ChannelContent[] {
  return channelContent.filter((c) => c.channelId === channelId);
}

export function getFeaturedContent(): ChannelContent[] {
  return channelContent.filter((c) => c.featured);
}

export function getLiveContent(): ChannelContent[] {
  return channelContent.filter((c) => c.isLive);
}

export function getExclusiveContent(): ChannelContent[] {
  return channelContent.filter((c) => c.isExclusive);
}

export function getSponsoredContent(): ChannelContent[] {
  return channelContent.filter((c) => c.isSponsored);
}

export function getContentByCategory(category: string): ChannelContent[] {
  return channelContent.filter((c) => c.category === category);
}

export function getChannelDistribution(): { channelId: string; count: number; config: ChannelTypeConfig | undefined }[] {
  return channelTypeConfigs.map((config) => ({
    channelId: config.id,
    count: getContentByChannel(config.id).length,
    config,
  }));
}

export function getContentTypeDistribution(channelId: string): { type: string; count: number }[] {
  const content = getContentByChannel(channelId);
  const types = [...new Set(content.map((c) => c.type))];
  return types.map((type) => ({
    type,
    count: content.filter((c) => c.type === type).length,
  })).sort((a, b) => b.count - a.count);
}